import { Message, User } from '@progress/kendo-react-conversational-ui';
import { Citation, DataSource } from '../models/elsie-ai.model';
import {
  getCitationsFromOrdinals,
  normalizeCitations,
  createCitationOrdinalsMap,
  extractDistinctDataSources,
} from './citationUtils';
import { aiUser, aiUserV2 } from '../components/widgets/widget-elsie-ai/constants';

// API Message interfaces
export interface ApiMessage {
  question?: string;
  response: {
    chatResponse: string;
    citations?: Citation[];
    citationOrdinals?: Record<string, number>;
    table?: any;
    visualization?: any;
    error?: any;
    time?: any;
    highlights?: any;
    followUp?: any[];
    keyInsights?: string[];
  };
  timestamp: number;
}

export interface KendoMessage extends Message {
  meta?: {
    citations: string[];
    citations2: Citation[];
    citationsOrder: Map<string, number>;
  };
  table?: any;
  visualization?: any;
  error?: any;
  time?: any;
  highlights?: any;
  followUp?: any[];
  dataSources?: DataSource[];
}

/**
 * Transforms an API message to Kendo UI message format
 */
export const transformApiMessageToKendo = (
  msg: ApiMessage,
  aiAuthor: User,
  userAuthor: User,
  messageTimestamp: Date
): KendoMessage[] => {
  const messages: KendoMessage[] = [];

  // Add user question if it exists
  if (msg.question) {
    messages.push({
      text: msg.question,
      author: userAuthor,
      timestamp: messageTimestamp,
    });
  }

  // Process citation ordinals
  const citationOrdinals = createCitationOrdinalsMap(msg.response.citationOrdinals || {});

  // Get ordered citations
  const orderedCitations = getCitationsFromOrdinals({
    citationOrdinals,
    citations: msg.response.citations || [],
  });

  const citationIds = orderedCitations.map((c) => c.id);

  // Normalize citation text
  let normalizedText = normalizeCitations(msg.response.chatResponse);

  // Add key insights for summarize conversations
  if (msg.response.keyInsights?.length) {
    msg.response.keyInsights.forEach((insight: string, index: number) => {
      normalizedText = `${normalizedText}\n${index === 0 ? '\n' : ''}${normalizeCitations(insight)}`;
    });
  }

  // Extract distinct data sources
  const distinctDataSources = extractDistinctDataSources(msg.response.citations || []);

  // Add AI response message
  messages.push({
    text: normalizedText,
    author: aiAuthor,
    timestamp: messageTimestamp,
    meta: {
      citations: citationIds,
      citations2: orderedCitations,
      citationsOrder: citationOrdinals,
    },
    table: msg.response.table,
    visualization: msg.response.visualization,
    error: msg.response.error,
    time: msg.response.time,
    highlights: msg.response.highlights,
    followUp: msg.response.followUp || [],
    dataSources: distinctDataSources,
  });

  return messages;
};

/**
 * Creates AI user object with optional response time
 */
export const getAiUser = (version: 1 | 2, responseTime?: string): User => {
  if (version === 2) {
    return {
      ...aiUserV2,
      name: `${aiUserV2.name || 'Elsie AI'}${responseTime ? ' ' + responseTime : ''}`,
    };
  }

  return {
    ...aiUser,
    name: aiUser.name || 'Elsie AI',
  };
};

/**
 * Creates user author object
 */
export const getUserAuthor = (user: { email?: string; externalName?: string }): User => {
  return {
    id: user?.email || 'user',
    name: user?.externalName || 'User',
  };
};
